import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Block,
  BlockTitle,
  Button,
  List,
  ListItem,
  Page,
  Popup,
  Preloader,
} from "framework7-react";
import PopupNavbar from "../../components/popup-navbar";
import { usePopupBackClose } from "../../hooks/use-back-close";
import { useOnline } from "../../hooks/use-online";
import { loadActivity } from "../../redux/reducer/activity";
import {
  activityParts,
  formatActivityTime,
  groupActivityByDay,
} from "../../utils/activity-text";
import "./activity.css";

const INITIAL = {
  status: "idle",
  entries: [],
  nextBefore: null,
  available: true,
  error: null,
  loadingMore: false,
  moreError: null,
};

const activityItem = (entry) => {
  const { subject, predicate } = activityParts(entry);
  return (
    <ListItem
      key={entry.id}
      className="activity-item"
      header={formatActivityTime(entry.created_at)}
    >
      <span slot="title" className="activity-text">
        <strong className="activity-actor">{subject}</strong> {predicate}
      </span>
    </ListItem>
  );
};

const ActivityPopup = ({ groupId, opened, onClose }) => {
  const dispatch = useDispatch();
  const online = useOnline();
  const backClose = usePopupBackClose(opened);
  const [state, setState] = useState(INITIAL);
  const latestRequest = useRef(0);

  const loadFirstPage = useCallback(() => {
    const request = ++latestRequest.current;
    setState({ ...INITIAL, status: "loading" });
    dispatch(
      loadActivity(
        groupId,
        null,
        (page) => {
          if (request !== latestRequest.current) return;
          setState({
            ...INITIAL,
            status: "loaded",
            entries: page?.entries ?? [],
            nextBefore: page?.next_before ?? null,
            available: page?.available !== false,
          });
        },
        (message) => {
          if (request !== latestRequest.current) return;
          setState({ ...INITIAL, status: "error", error: message });
        },
      ),
    );
  }, [dispatch, groupId]);

  const loadMore = () => {
    const request = ++latestRequest.current;
    setState((current) => ({ ...current, loadingMore: true, moreError: null }));
    dispatch(
      loadActivity(
        groupId,
        state.nextBefore,
        (page) => {
          if (request !== latestRequest.current) return;
          setState((current) => ({
            ...current,
            loadingMore: false,
            entries: [...current.entries, ...(page?.entries ?? [])],
            nextBefore: page?.next_before ?? null,
          }));
        },
        (message) => {
          if (request !== latestRequest.current) return;
          setState((current) => ({ ...current, loadingMore: false, moreError: message }));
        },
      ),
    );
  };

  const needsLoad = state.status === "idle" || state.status === "offline";
  useEffect(() => {
    if (!opened) {
      latestRequest.current += 1;
      return;
    }
    if (!needsLoad || groupId == null) return;
    if (online) loadFirstPage();
    else setState((current) => (current.status === "offline" ? current : { ...INITIAL, status: "offline" }));
  }, [opened, online, needsLoad, groupId, loadFirstPage]);

  const days = useMemo(() => groupActivityByDay(state.entries), [state.entries]);
  const { status } = state;

  return (
    <Popup
      {...backClose}
      className="activity-popup"
      opened={opened}
      onPopupClosed={() => {
        setState(INITIAL);
        onClose();
      }}
    >
      <Page>
        <PopupNavbar title="Änderungsverlauf" />
        <div className="activity-content">
          {!online && (
            <Block key="offline" className="activity-offline">
              Der Verlauf ist nur online verfügbar. Sobald du wieder online
              bist, wird er geladen.
            </Block>
          )}
          {status === "loading" && (
            <Block key="loading" className="activity-loading">
              <Preloader />
            </Block>
          )}
          {status === "error" && (
            <Block key="error" className="activity-error">
              <p>{state.error}</p>
              <Button tonal onClick={loadFirstPage} disabled={!online}>
                Erneut versuchen
              </Button>
            </Block>
          )}
          {status === "loaded" && !state.available && (
            <Block key="unavailable" className="activity-empty">
              Der Verlauf ist auf dem Server noch nicht eingerichtet.
            </Block>
          )}
          {status === "loaded" && state.available && state.entries.length === 0 && (
            <Block key="empty" className="activity-empty">
              Noch keine Änderungen. Ab jetzt siehst du hier, wer etwas
              hinzugefügt, geändert oder gelöscht hat.
            </Block>
          )}
          {days.map((day) => (
            <div className="activity-day" key={day.key} data-day={day.key}>
              <BlockTitle className="activity-day-title">{day.label}</BlockTitle>
              <List strong inset dividers className="activity-list">
                {day.entries.map(activityItem)}
              </List>
            </div>
          ))}
          {state.nextBefore != null && (
            <Block key="more" className="activity-more">
              {state.moreError && <p className="activity-more-error">{state.moreError}</p>}
              <Button
                tonal
                onClick={loadMore}
                disabled={state.loadingMore || !online}
              >
                {state.loadingMore ? <Preloader size={20} /> : "Mehr laden"}
              </Button>
            </Block>
          )}
        </div>
      </Page>
    </Popup>
  );
};

export default ActivityPopup;
